import { useNigoAnalysisContext } from '@client/contexts/NigoAnalysisContext';
import {
  AlertTriangle,
  CheckCircle2,
  CircleX,
  ChevronUp,
  ArrowUpRight,
  Loader2,
  RefreshCw,
  Edit,
} from 'lucide-react';
import { useMemo } from 'react';
import { Button } from './ui/button';
import QuestionField from './QuestionField';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

type NigoSeverity = 'critical' | 'warning' | 'suggestion';
type IssueSource = 'rule' | 'ai';

interface NigoIssue {
  severity: NigoSeverity;
  source: IssueSource;
  category: RuleCategory;
  message: string;
  suggestedFix?: string;
  refs: string[];
}

export function NigoAnalysis() {
  const { data, actions, ui } = useNigoAnalysisContext();

  const questionRegistryToUse = data.metadata.formDefinition.questions;
  const formDataToUse = data.metadata.formData;
  const parametersToUse = data.metadata.parameters ?? {};
  const isLoading = ui.isLoading;
  const error = ui.error;
  const aiResult = data.aiResult;
  const ruleResult = data.ruleResult;
  const resolvedIssues = data.resolvedIssues;
  const lastRunTimestamp = data.lastRunTimestamp;
  const editedQuestions = data.metadata.editedQuestions;
  const hasCompletedFormOnce = data.metadata.hasCompletedFormOnce;
  const expandedIssues = ui.expandedIssues;
  const accordionValue = ui.accordionValue;
  const awaitingRecheck = data.metadata.awaitingRecheck;
  const analyzingAwaitingRecheck = data.metadata.analyzingAwaitingRecheck;
  const handleSubmissionChange = actions.handleSubmissionChange;
  const handleJumpToQuestion = actions.handleJumpToQuestion;
  const handleRerunAnalysis = actions.handleRerunAnalysis;
  const onReviewCompleteAndSubmit = actions.handleReviewCompleteAndSubmit;
  const toggleIssue = actions.toggleIssue;
  const setAccordionValue = actions.setAccordionValue;
  const handleSaveIssueChanges = actions.handleSaveChanges;
  const handleCancelIssueChanges = actions.handleCancelChanges;

  const mapSeverity = (severity?: RuleSeverity): NigoSeverity => {
    if (severity === 'high-confidence') return 'critical';
    if (severity === 'medium-confidence') return 'warning';
    return 'suggestion';
  };

  const unifiedIssues = useMemo(() => {
    const issues: NigoIssue[] = [];

    ruleResult?.forEach(rule => {
      if (rule.ok) return;
      issues.push({
        severity: mapSeverity(rule.severity),
        source: 'rule',
        category: (rule.category || 'general') as RuleCategory,
        message: rule.message || '',
        refs: rule.refs || [],
      });
    });

    aiResult?.issues?.forEach(issue => {
      issues.push({
        severity: mapSeverity(issue.severity),
        source: 'ai',
        category: issue.category || 'general',
        message: issue.message,
        suggestedFix: issue.suggestedFix,
        refs: issue.refs || [],
      });
    });

    return issues;
  }, [ruleResult, aiResult]);

  const groupedIssues = useMemo(() => {
    const grouped = {
      critical: [] as NigoIssue[],
      warning: [] as NigoIssue[],
      suggestion: [] as NigoIssue[],
    };

    unifiedIssues.forEach(issue => {
      if (issue.severity === 'critical') {
        grouped.critical.push(issue);
      } else if (issue.severity === 'warning') {
        grouped.warning.push(issue);
      } else {
        grouped.suggestion.push(issue);
      }
    });

    return grouped;
  }, [unifiedIssues]);

  const unifiedResolvedIssues = useMemo(() => {
    const resolved: NigoIssue[] = [];

    resolvedIssues?.forEach(rule => {
      resolved.push({
        severity: mapSeverity(rule.severity),
        source: 'rule',
        category: (rule.category || 'general') as RuleCategory,
        message: rule.message || '',
        refs: rule.refs || [],
      });
    });

    aiResult?.resolvedIssues?.forEach(issue => {
      resolved.push({
        severity: mapSeverity(issue.severity),
        source: 'ai',
        category: issue.category || 'general',
        message: issue.message,
        suggestedFix: issue.suggestedFix,
        refs: issue.refs || [],
      });
    });

    return resolved;
  }, [resolvedIssues, aiResult]);

  const getRelativeTime = (timestamp: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  const getCategoryLabel = (category: RuleCategory): string => {
    const labels: Record<RuleCategory, string> = {
      'missing-information': 'Missing Information',
      'formatting': 'Formatting',
      'conflicting-information': 'Conflicting Information',
      'data-consistency': 'Data Consistency',
      'carrier-requirements': 'Carrier Requirements',
      'state-requirements': 'State Requirements',
      'disclosure-issues': 'Disclosure Issues',
      'general': 'General',
    };
    return labels[category] || category;
  };

  const getCategoryBadge = (category: RuleCategory) => {
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
        {getCategoryLabel(category)}
      </span>
    );
  };

  const getSourceBadge = (source: IssueSource) => {
    if (source === 'ai') {
      return (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
          AI-detected
        </span>
      );
    }
  };

  const getReRunCTA = () => {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={e => {
          e.stopPropagation();
          handleRerunAnalysis();
        }}
        disabled={isLoading}
        className="ml-auto mr-2 h-7 gap-2 text-xs"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            Re-Checking Answers...
          </>
        ) : (
          <>
            <RefreshCw className="h-3 w-3" />
            Re-check Answers
          </>
        )}
      </Button>
    );
  };

  const renderIssue = (issue: NigoIssue, index: number, severityGroup: string) => {
    const issueKey = `${severityGroup}-${issue.source}-${index}`;
    const relatedQuestions =
      issue.refs
        ?.map(ref => questionRegistryToUse?.[ref])
        .filter((q): q is PolicyFormQuestion => q !== undefined) ?? [];

    const hasEditedQuestions = relatedQuestions.some(q => editedQuestions.has(q.questionRef));
    const isAwaitingRecheck = awaitingRecheck.has(issueKey);
    const isCollapsed = !expandedIssues.has(issueKey) || (isLoading && isAwaitingRecheck);

    const getBarColor = () => {
      if (issue.severity === 'critical') return 'bg-red-500';
      if (issue.severity === 'warning') return 'bg-yellow-500';
      return 'bg-blue-500';
    };

    return (
      <li
        key={index}
        className={`rounded-lg border border-gray-200 transition-colors hover:border-gray-300 ${
          isAwaitingRecheck && isCollapsed ? 'opacity-50' : ''
        }`}
      >
        <div className="flex items-start gap-3 p-4">
          <div className={`w-1 self-stretch rounded-full ${getBarColor()}`} />
          <div
            className={`flex flex-1 items-start gap-3 ${
              isAwaitingRecheck && isCollapsed ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <div className="flex-1 space-y-2">
              <p className="text-primary-dark whitespace-pre-line text-sm">{issue.message}</p>
              {issue.suggestedFix && (
                <p className="text-primary-dark text-xs">
                  <span className="font-semibold">Suggested Fix:</span> {issue.suggestedFix}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAwaitingRecheck && isCollapsed && (
              <span
                className="text-primary-dark flex cursor-help items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-medium"
                title="You've edited answers related to this issue. Click 'Re-check Answers' to verify if this issue is resolved."
              >
                {isLoading && analyzingAwaitingRecheck.has(issueKey) ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Rechecking answer
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Awaiting recheck
                  </>
                )}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleIssue(issueKey)}
              disabled={isAwaitingRecheck}
              className="h-8 flex-shrink-0 gap-2 text-xs font-semibold"
            >
              {isCollapsed ? (
                <>
                  <Edit className="h-3 w-3" />
                  Edit Answer{relatedQuestions.length > 1 ? 's' : ''}
                </>
              ) : (
                <ChevronUp className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="space-y-3 border-t px-4 pb-4 pt-3">
            <div className="flex flex-wrap gap-2">
              {getSourceBadge(issue.source)}
              {getCategoryBadge(issue.category)}
            </div>

            {relatedQuestions.length > 0 && (
              <div className="space-y-3">
                {relatedQuestions.map(question => {
                  return (
                    <div
                      key={question.questionRef}
                      className="relative z-50 rounded-lg border border-gray-200 p-3"
                    >
                      {handleJumpToQuestion && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleJumpToQuestion(question.questionRef);
                          }}
                          className="text-primary-dark absolute right-2 top-2 h-7 gap-1 px-2 text-xs hover:bg-blue-50"
                        >
                          <ArrowUpRight className="h-3 w-3" />
                          Jump to question
                        </Button>
                      )}
                      <div className="pr-36 text-gray-700">
                        <QuestionField
                          question={question}
                          value={formDataToUse?.[question.questionRef]}
                          onChange={handleSubmissionChange(question.questionRef)}
                          parameters={parametersToUse}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-start gap-2 pt-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleSaveIssueChanges(issueKey, relatedQuestions)}
                    disabled={!hasEditedQuestions}
                    className="bg-primary hover:bg-primary/90 h-8 gap-2 text-xs"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancelIssueChanges(relatedQuestions)}
                    disabled={!hasEditedQuestions}
                    className="h-8 gap-2 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderResolvedIssue = (issue: NigoIssue, index: number) => {
    return (
      <div
        key={index}
        className="rounded-lg border border-gray-200 p-3 transition-colors hover:border-gray-300"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Resolved
              </span>
              {getSourceBadge(issue.source)}
              {getCategoryBadge(issue.category)}
            </div>
            <p className="text-primary-dark whitespace-pre-line text-sm">{issue.message}</p>
            {issue.suggestedFix && (
              <div className="rounded bg-blue-50 p-2 text-xs text-blue-900">
                <span className="font-semibold">Suggested Fix:</span> {issue.suggestedFix}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const hasCriticalIssues = groupedIssues.critical.length > 0;
  const hasWarningsOrSuggestions =
    groupedIssues.warning.length > 0 || groupedIssues.suggestion.length > 0;
  const hasResolvedIssues = unifiedResolvedIssues.length > 0;

  const criticalHasRechecks = useMemo(
    () =>
      groupedIssues.critical.some((issue, index) => {
        const issueKey = `critical-${issue.source}-${index}`;
        return awaitingRecheck.has(issueKey);
      }),
    [groupedIssues.critical, awaitingRecheck],
  );
  const warningsHasRechecks = useMemo(
    () =>
      [...groupedIssues.warning, ...groupedIssues.suggestion].some((issue, index) => {
        const issueKey = `warning-${issue.source}-${index}`;
        return awaitingRecheck.has(issueKey);
      }),
    [groupedIssues.warning, groupedIssues.suggestion, awaitingRecheck],
  );

  return (
    <div className="flex min-h-full flex-1 flex-col overflow-auto">
      <div className="flex flex-1 flex-col space-y-6 py-4">
        {/* Disclaimer */}
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="text-primary-dark text-xs">
            <p className="mb-2">
              <span className="font-semibold">Disclaimer:</span> This analysis combines automated
              checks with AI-powered insights to identify potential issues with the application.
            </p>
            <ul className="ml-4 list-disc space-y-1 pl-2">
              <li className="pl-1">
                <span className="font-semibold">AI can make mistakes.</span> Please review all
                suggestions carefully and verify accuracy before submission.
              </li>
              <li className="pl-1">
                <span className="font-semibold">This is not a coverage guarantee. </span>
                <span className="font-semibold">Automated checks</span> validate form data against
                carrier requirements. <span className="font-semibold">AI insights</span> provide
                additional risk analysis.
              </li>
              <li className="pl-1">
                <span className="font-semibold">These are observations, not advice.</span> This tool
                provides informational insights based on application data and carrier guidelines. It
                is not professional insurance, financial, medical, or legal advice.
              </li>
            </ul>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center space-x-2">
              <CircleX className="h-5 w-5 text-red-400" />
              <h3 className="text-sm font-medium text-red-800">Analysis Error</h3>
            </div>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        )}

        <Accordion
          type="multiple"
          value={accordionValue}
          onValueChange={setAccordionValue}
          className="space-y-4"
        >
          {hasCriticalIssues && (
            <div>
              <AccordionItem
                value="critical"
                className="rounded-lg"
              >
                <AccordionTrigger className="hover:bg-gray-50">
                  <div className="flex flex-1 items-center gap-3">
                    <CircleX className="h-5 w-5 text-red-600" />
                    <span className="text-primary-dark text-base font-medium">
                      Errors that need attention
                    </span>
                    <span className="text-primary-dark rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium">
                      {groupedIssues.critical.length}
                    </span>
                    {criticalHasRechecks && getReRunCTA()}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <ul className="space-y-3">
                    {groupedIssues.critical.map((issue, index) =>
                      renderIssue(issue, index, 'critical'),
                    )}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </div>
          )}

          {/* Warnings & Suggestions Section */}
          {hasWarningsOrSuggestions && (
            <div>
              <AccordionItem
                value="warnings"
                className="rounded-lg"
              >
                <AccordionTrigger className="hover:bg-gray-50">
                  <div className="flex flex-1 items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <span className="text-primary-dark text-base font-medium">
                      Non-blocking warnings & suggestions
                    </span>
                    <span className="text-primary-dark rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium">
                      {groupedIssues.warning.length + groupedIssues.suggestion.length}
                    </span>
                    {warningsHasRechecks && getReRunCTA()}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <ul className="space-y-3">
                    {groupedIssues.warning.map((issue, index) =>
                      renderIssue(issue, index, 'warning'),
                    )}
                    {groupedIssues.suggestion.map((issue, index) =>
                      renderIssue(issue, index, 'warning'),
                    )}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </div>
          )}

          {/* Resolved Issues Section */}
          {hasResolvedIssues && (
            <div>
              <AccordionItem
                value="resolved"
                className="rounded-lg"
              >
                <AccordionTrigger className="hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-primary-dark text-base font-medium">Resolved Items</span>
                    <span className="text-primary-dark rounded-full bg-gray-100 px-3 py-0.5 text-xs font-medium">
                      {unifiedResolvedIssues.length}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <div className="space-y-3">
                    {unifiedResolvedIssues.map((issue, index) => renderResolvedIssue(issue, index))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          )}
        </Accordion>

        {/* Empty State */}
        {!hasCriticalIssues &&
          !hasWarningsOrSuggestions &&
          !hasResolvedIssues &&
          !isLoading &&
          !error && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-green-600" />
              <p className="text-base font-medium text-green-800">
                No issues found - Application looks good!
              </p>
            </div>
          )}

        {/* Action Buttons (Bottom Right) */}
        {hasCompletedFormOnce && (
          <div className="mt-6 flex justify-end gap-3">
            <Button
              onClick={onReviewCompleteAndSubmit}
              size="lg"
              variant={hasCriticalIssues || hasWarningsOrSuggestions ? 'outline' : 'default'}
              className={
                hasCriticalIssues || hasWarningsOrSuggestions
                  ? ''
                  : 'bg-primary hover:bg-primary/90'
              }
            >
              {hasCriticalIssues || hasWarningsOrSuggestions
                ? 'Proceed without review'
                : 'Review Complete - Submit'}
            </Button>
            <div>
              <Button
                variant={hasCriticalIssues || hasWarningsOrSuggestions ? 'default' : 'outline'}
                size="lg"
                onClick={handleRerunAnalysis}
                disabled={isLoading || (!hasCriticalIssues && !hasWarningsOrSuggestions)}
                className={
                  hasCriticalIssues || hasWarningsOrSuggestions
                    ? 'bg-primary hover:bg-primary/90'
                    : ''
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Re-Checking Answers...
                  </>
                ) : hasCriticalIssues || hasWarningsOrSuggestions ? (
                  <>
                    Re-check Answers
                    {lastRunTimestamp && (
                      <span className="ml-2 text-xs">
                        (Last checked: {getRelativeTime(lastRunTimestamp)})
                      </span>
                    )}
                  </>
                ) : (
                  'Analysis complete'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
